// ============================================
// F-06 扫码登录（已登录端确认页）：/qr-confirm?ticket=xxx
// 已登录用户扫码（或打开链接）后在此确认，新端即可领取登录态。
// 未登录则跳转登录页并携带回跳地址。
// ============================================
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Result, Space, Typography, message } from 'antd';
import { CheckCircleOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { confirmQrLogin, scanQrLogin } from '../api/qrLogin';
import useAuthStore from '../stores/authStore';

const { Title, Text, Paragraph } = Typography;

export default function QrConfirmPage() {
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get('ticket') || '';
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 已扫码提示（可选）：告知新端"已扫码待确认"
  useEffect(() => {
    if (!isAuthenticated || !ticket || done) return;
    scanQrLogin(ticket).catch(() => {});
  }, [isAuthenticated, ticket, done]);

  if (!isAuthenticated) {
    const back = `/qr-confirm?ticket=${encodeURIComponent(ticket)}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(back)}`} replace />;
  }

  if (!ticket) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f1a' }}>
        <Card style={{ width: 420, background: '#1a1a2e', borderColor: '#30475e' }}>
          <Result status="error" title="缺少 ticket" subTitle="请重新在新设备上生成二维码" />
        </Card>
      </div>
    );
  }

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      await confirmQrLogin(ticket);
      setDone(true);
      message.success('已确认，新设备将自动登录');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || '确认失败，请重试';
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f1a' }}>
      <Card style={{ width: 440, background: '#1a1a2e', borderColor: '#30475e' }} styles={{ body: { padding: 28 } }}>
        {done ? (
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="确认成功"
            subTitle="新设备正在登录，请回到新设备查看（本页可关闭）"
            data-testid="qr-confirm-done"
          />
        ) : (
          <Space orientation="vertical" size={16} style={{ width: '100%', textAlign: 'center' }}>
            <Title level={4} style={{ margin: 0, color: '#e6f4ff' }}>
              <SafetyCertificateOutlined style={{ marginRight: 8, color: '#4e9eff' }} />
              确认登录到新设备
            </Title>
            <Paragraph style={{ color: '#aaa', marginBottom: 0 }}>
              当前账号：<Text style={{ color: '#4e9eff' }}>{user?.username || '-'}</Text>
              <br />
              确认后，新设备将以此账号登录（其他会话按现有互踢策略处理）。
            </Paragraph>
            {error && <Text type="danger" data-testid="qr-confirm-error">{error}</Text>}
            <Space>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleConfirm}
                data-testid="qr-confirm-btn"
              >
                确认登录
              </Button>
              <Button onClick={() => window.close()} data-testid="qr-confirm-cancel">取消</Button>
            </Space>
            <Text style={{ color: '#666', fontSize: 12 }}>若非本人操作，请勿确认并尽快修改密码</Text>
          </Space>
        )}
      </Card>
    </div>
  );
}
